/**
 * Politica de protecao de PII para o tracking do Wings.
 *
 * Wings e um app fintech submetido a LGPD. Nenhum dos campos abaixo pode
 * ser enviado a qualquer fornecedor de analytics como property de evento
 * ou identificador. Esta lista funciona como ultima linha de defesa: o
 * codigo de instrumentacao ja deve evitar coletar esses dados, mas o
 * sanitizador aqui remove silenciosamente se algo escapar.
 */
export const PII_KEYS = new Set<string>([
  'cpf',
  'cnpj',
  'email',
  'e_mail',
  'phone',
  'phone_number',
  'telefone',
  'password',
  'pwd',
  'senha',
  'token',
  'id_token',
  'refresh_token',
  'access_token',
  'jwt',
  'authorization',
  'name',
  'full_name',
  'first_name',
  'last_name',
  'birthday',
  'date_of_birth',
  'address',
  'street',
  'zipcode',
  'cep',
  'document',
  'rg',
  'account_number',
  'agency',
  'card_number',
  'cvv',

  'balance',
  'saldo',
  'amount_brl',
  'amount_value',
  'value_brl',
  'transaction_value',
]);

/**
 * Regex que pega o que parece ser PII bruta (CPF, CNPJ, e-mail) mesmo que a
 * chave nao esteja na lista acima. Usado como fallback.
 */
const PII_VALUE_PATTERNS: RegExp[] = [
  /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/, // CPF
  /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/, // CNPJ
  /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/, // email
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/, // JWT
];

const MAX_STRING_LENGTH = 256;

export type Properties = Record<string, unknown>;

/**
 * Remove ou trunca campos com PII. Retorna SEMPRE um objeto novo, nunca muta
 * o input. Substitui valores suspeitos por `[redacted]`.
 */
export function sanitizeProperties(input?: Properties): Properties {
  if (!input) return {};

  const out: Properties = {};
  for (const [rawKey, value] of Object.entries(input)) {
    const key = rawKey.toLowerCase();

    if (PII_KEYS.has(key)) {
      continue;
    }

    out[rawKey] = sanitizeValue(value);
  }
  return out;
}

function sanitizeValue(value: unknown): unknown {
  if (value == null) return value;

  if (typeof value === 'string') {
    for (const pattern of PII_VALUE_PATTERNS) {
      if (pattern.test(value)) return '[redacted]';
    }
    return value.length > MAX_STRING_LENGTH
      ? value.slice(0, MAX_STRING_LENGTH)
      : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeValue);
  }

  if (typeof value === 'object') {
    return sanitizeProperties(value as Properties);
  }

  return value;
}
